import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import Button from '../Button/Button';
import './Modal.css';

const modalStack = [];
let openModalCount = 0;


const Modal = ({
  show,
  onHide,
  children,
  size = 'lg', // 'sm', 'md', 'lg', 'xl',
  position = 'top', // 'center', 'top', 'bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end',
  backdrop = true,
  keyboard = true,
  className = '',
  style = {}
}) => {

  const onHideRef = useRef(onHide);

  // This prevents having to include onHide in the 2nd useEffect since it causes indefinite re-rendering
  useEffect(() => {
    if (show) {
      onHideRef.current = onHide;
    }
  }, [show, onHide]);

  // Handle Modal close on escape key
  useEffect(() => {
    if (!show) return;

    modalStack.push(onHideRef);

    const handleEscape = (e) => {
      if (keyboard && e.key === 'Escape') {
        const topModalRef = modalStack[modalStack.length - 1];
        topModalRef?.current?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      modalStack.pop();
    }
  }, [show, keyboard]);
  
  const lockBodyScroll = () => {
    openModalCount++;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    // Prevent scroll shift by adding padding
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
    document.body.style.overflow = 'hidden';
  }

  // Enable body scroll if open modal count <= 0  
  const unlockBodyScroll = () => {
    openModalCount--;
    if (openModalCount <= 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      openModalCount = 0; // reset
    }
  };

  useEffect(() => {
    if (!show) return;

    lockBodyScroll();
    return unlockBodyScroll;
  }, [show])

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && backdrop === true) {
      onHide();
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'modal-sm';
      case 'md':
        return 'modal-md';
      case 'lg':
        return 'modal-lg';
      case 'xl':
        return 'modal-xl';
      default:
        return 'modal-lg';
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'top': return 'modal-top';
      case 'bottom': return 'modal-bottom';
      case 'top-start': return 'modal-top-start';
      case 'top-end': return 'modal-top-end';
      case 'bottom-start': return 'modal-bottom-start';
      case 'bottom-end': return 'modal-bottom-end';
      case 'center': return 'modal-centered';
      default: return 'modal-top';
    }
  };

  // if (!show) return null;
  const [visible, setVisible] = useState(show);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    let timeout;
    if (show) {
      setVisible(true);
      timeout = setTimeout(() => setAnimate(true), 10);
    } else if (visible) {
      setAnimate(false);
      timeout = setTimeout(() => setVisible(false), 300);
    }
    return () => clearTimeout(timeout);
  }, [show, visible]);

  if (!visible) return null;

  const modalContent = (
    <div
      className={`modal-backdrop ${getPositionClasses()}
      ${ animate ? 'fade-in' : 'fade-out' }` }
      onClick={handleBackdropClick}
    >
      <div
        className={`modal-container ${getSizeClasses()} ${className} 
        ${ animate ? 'slide-in' : 'slide-out'}`}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {React.Children.map(children, (child) => {
        // Automatically pass onHide to Modal.Header
        if (child.type?.displayName === 'ModalHeader') {
          return React.cloneElement(child, { onHide });
        }
        return child;
        })}
      </div>
    </div>
  );

  // Render modal outside the normal DOM tree
  return ReactDOM.createPortal(modalContent, document.body);
};

const ModalHeader = ({ children, closeButton = true, onHide, className = '' }) => (
  <div className={`modal-header ${className}`}>
    <div className="modal-header-title">{children}</div>
    {closeButton && onHide && (
      <Button size='sm' variant='ghost' onClick={onHide} className='modal-close-btn'>
        &times;
      </Button>
    )}
  </div>
);
ModalHeader.displayName = 'ModalHeader';

const ModalBody = ({ children, className = '' }) => (
  <div className={`modal-body ${className}`}>{children}</div>
);

const ModalFooter = ({ children, className = '' }) => (
  <div className={`modal-footer ${className}`}>{children}</div>
);

const ModalTitle = ({ children, as: Component = 'h4', className = '' }) => {
  const baseClasses = 'modal-title';
  return React.createElement(Component, { className: `${baseClasses} ${className}` }, children);
};

// Attach sub-components
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Title = ModalTitle;

export default Modal;